import { useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Empty, Flex, Image, Input, Modal, Segmented, Select, Skeleton, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../app/api-context';
import { VocabHint } from '../../components/VocabHint';
import { CATEGORY_LABEL, PROGRESS_LABEL, PURPOSE_LABEL, STATUS_LABEL, themeMatches, themeOptions, matchesUnified, onlyOtherPaths, type AppSearchRef, type UnifiedList as UnifiedData, type UnifiedRow, type UnifiedStatus } from '../../lib/vocab-types';

type StatusFilter = 'all' | UnifiedStatus;
const CHECKER = 'repeating-conic-gradient(#f3f3f3 0% 25%, #fff 0% 50%) 50% / 12px 12px';
const THUMB = 48;

function Thumb({ url, title }: { url: string | null; title: string }) {
  if (!url) {
    return (
      <Tooltip title={`${title} · 앱 번들 이미지(CDN 없음)`}>
        <div style={{ width: THUMB, height: THUMB, background: '#fafafa', border: '1px dashed #d9d9d9', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>번들</div>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={title}>
      <Image src={url} width={THUMB} height={THUMB} style={{ objectFit: 'contain', background: CHECKER }} alt={title} />
    </Tooltip>
  );
}

function SearchThumbs({ items, max = 3 }: { items: AppSearchRef[]; max?: number }) {
  if (!items.length) return <Typography.Text type="secondary">-</Typography.Text>;
  return (
    <Image.PreviewGroup>
      <Flex gap={4} align="center">
        {items.slice(0, max).map((i) => <Thumb key={i.docId} url={i.imageUrl} title={`${i.displayName} · ${i.fileId ?? i.docId} · 태그: ${i.tags.join(', ')}`} />)}
        {items.length > max && <Typography.Text type="secondary">+{items.length - max}</Typography.Text>}
      </Flex>
    </Image.PreviewGroup>
  );
}

type LinkAction = 'link' | 'unlink' | 'reset';
interface DetailItem { key: string; linkKey: string; url: string | null; label: string; sub: string; tag?: string; importable?: boolean }

/** 펼친 줄: 연결된 이미지·확인 필요·관련 이미지를 크게. 관리 중 어휘는 연결을 확정·해제할 수 있다 */
function Detail({ r, onLink, busy, imported, onImport, onRemake }: { r: UnifiedRow; onLink?: (key: string, action: LinkAction) => void; busy?: boolean; imported?: Set<string>; onImport?: (keys: string[]) => void; onRemake?: (key: string, url: string | null) => void }) {
  const canLink = !!onLink && !!r.vocabId;
  const canImport = !!onImport && !!r.vocabId;
  const section = (title: string, items: DetailItem[], action: { label: string; act: LinkAction } | null, hint?: string) =>
    items.length > 0 && (
      <div style={{ marginBottom: 12 }}>
        <Typography.Text strong style={{ fontSize: 13 }}>{title}</Typography.Text>
        {hint && <Typography.Text type="secondary" style={{ fontSize: 12 }}> {hint}</Typography.Text>}
        <Image.PreviewGroup>
          <Flex wrap gap={12} style={{ marginTop: 6 }}>
            {items.map((x) => (
              <div key={x.key} style={{ width: 120 }}>
                {x.url ? <Image src={x.url} width={120} height={120} style={{ objectFit: 'contain', background: CHECKER }} alt={x.label} /> : <div style={{ width: 120, height: 120, background: '#fafafa', border: '1px dashed #d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>앱 번들 이미지</div>}
                <Typography.Text style={{ fontSize: 12, display: 'block' }} ellipsis>{x.label}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }} ellipsis={{ tooltip: x.sub }}>{x.sub}</Typography.Text>
                {x.tag && <Tag style={{ fontSize: 11, marginTop: 2 }}>{x.tag}</Tag>}
                {x.importable && imported?.has(x.linkKey) && <Tag color="cyan" style={{ fontSize: 11, marginTop: 2 }}>어드민에 가져옴</Tag>}
                {x.importable && imported?.has(x.linkKey) && onRemake && r.vocabId && (
                  <Button size="small" type="link" style={{ padding: 0, display: 'block' }} onClick={() => onRemake(x.linkKey, x.url)} aria-label={`참고해 새로 만들기 ${x.label}`}>
                    참고해 새로 만들기
                  </Button>
                )}
                {x.importable && !x.url && <Tag style={{ fontSize: 11, marginTop: 2 }}>앱 번들(가져올 수 없음)</Tag>}
                {x.importable && canImport && x.url && !imported?.has(x.linkKey) && (
                  <Button size="small" type="link" style={{ padding: 0, display: 'block' }} disabled={busy} onClick={() => onImport!([x.linkKey])} aria-label={`어드민으로 가져오기 ${x.label}`}>
                    어드민으로 가져오기
                  </Button>
                )}
                {canLink && action && (
                  <Button size="small" type="link" style={{ padding: 0, display: 'block' }} disabled={busy} onClick={() => onLink!(x.linkKey, action.act)} aria-label={`${action.label} ${x.label}`}>
                    {action.label}
                  </Button>
                )}
              </div>
            ))}
          </Flex>
        </Image.PreviewGroup>
      </div>
    );
  const manual = new Set(r.manualLinks ?? []);
  const card = (c: NonNullable<UnifiedRow['card']>): DetailItem => {
    const linkKey = `card:${c.cardId}`;
    return { key: linkKey, linkKey, url: c.imageUrl, label: `기본 카드 · ${c.text}`, sub: c.cardId, tag: r.vocabId ? (manual.has(linkKey) ? '확정 연결' : '자동 연결') : undefined };
  };
  const img = (i: AppSearchRef, withTag = true): DetailItem => {
    const linkKey = `search:${i.docId}`;
    return { key: i.docId, linkKey, url: i.imageUrl, label: `검색 · ${i.displayName}`, sub: `${i.path ?? '-'}/${i.fileId ?? i.docId} · ${i.tags.join(', ')}`, tag: withTag && r.vocabId ? (manual.has(linkKey) ? '확정 연결' : '자동 연결') : undefined };
  };
  const plain = (x: DetailItem): DetailItem => ({ ...x, tag: undefined });
  const excluded = r.excluded ?? { cards: [], searchImages: [] };
  const linkedItems = [...(r.card ? [card(r.card)] : []), ...r.searchImages.map((i) => img(i))].map((x) => ({ ...x, importable: true }));
  const notImported = linkedItems.filter((x) => x.url && !imported?.has(x.linkKey));
  const empty = !r.card && !r.searchImages.length && !r.needsConfirm.cards.length && !r.needsConfirm.searchImages.length && !r.related.length && !excluded.cards.length && !excluded.searchImages.length;
  return (
    <div data-testid={`detail-${r.key}`}>
      {canImport && notImported.length > 1 && (
        <Button size="small" style={{ marginBottom: 8 }} disabled={busy} onClick={() => onImport!(notImported.map((x) => x.linkKey))}>
          연결된 앱 이미지 {notImported.length}장 모두 어드민으로 가져오기(비용 없음)
        </Button>
      )}
      {!r.vocabId && linkedItems.some((x) => x.url) && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 6 }}>
          이 앱 이미지를 어드민으로 가져오려면 줄을 선택해 '관리 어휘로 저장'하세요(저장할 때 함께 가져올 수 있습니다).
        </Typography.Paragraph>
      )}
      {section('연결된 앱 이미지', linkedItems, { label: '연결 해제', act: 'unlink' })}
      {section('연결 확인 필요', [...r.needsConfirm.cards.map(card), ...r.needsConfirm.searchImages.map((i) => img(i))].map(plain), { label: '이 어휘에 연결', act: 'link' },
        canLink ? '같은 표기의 어휘가 여러 개(동음이의어)라 자동 연결하지 않았습니다. 이 뜻이 맞으면 연결하세요.' : '같은 표기의 어휘가 여러 개(동음이의어)라 자동 연결하지 않았습니다. 관리 어휘로 저장하면 연결을 확정할 수 있습니다.')}
      {section('연결 해제한 이미지', [...excluded.cards.map(card), ...excluded.searchImages.map((i) => img(i))].map(plain), { label: '자동 연결로 되돌리기', act: 'reset' })}
      {section('관련 이미지', r.related.map((i) => img(i, false)), { label: '이 어휘에 연결', act: 'link' }, '태그만 같은 검색 이미지(연결 아님)')}
      {empty && <Typography.Text type="secondary">앱에 등록된 이미지가 없습니다.</Typography.Text>}
    </div>
  );
}

interface AppChoice { category: string | null; vocabType: 'core' | 'fringe' }

/** 줄에 연결된 앱 이미지 중 CDN에 있어 가져올 수 있는 것(card:… / search:…) */
export const importableKeys = (r: UnifiedRow) => [
  ...(r.card?.imageUrl ? [`card:${r.card.cardId}`] : []),
  ...r.searchImages.filter((i) => i.imageUrl).map((i) => `search:${i.docId}`),
];

/** 후보·'앱에만 있음'을 관리 어휘로 저장(생성 요청 전에도). 앱 항목은 카테고리·핵심/주변을 정한다 */
function SaveDialog({ rows, purpose, onClose, onSaved }: { rows: UnifiedRow[]; purpose: 'save' | 'generate' | null; onClose: () => void; onSaved: (ids: string[]) => void }) {
  const api = useApi();
  const [choices, setChoices] = useState<Record<string, AppChoice>>({});
  const [withImages, setWithImages] = useState(true);
  const pending = rows.filter((r) => r.status !== 'managed');
  const choice = (r: UnifiedRow): AppChoice => choices[r.key] ?? { category: r.category, vocabType: 'fringe' };
  const missing = pending.filter((r) => r.status === 'app_only' && !choice(r).category);
  const save = useMutation({
    mutationFn: async () => {
      const ids = rows.filter((r) => r.vocabId).map((r) => r.vocabId!);
      const notes: string[] = [];
      const cands = pending.filter((r) => r.status === 'candidate').map((r) => r.candidateKey!);
      if (cands.length) ids.push(...(await api.post<{ ids: string[] }>('/vocab/import', { keys: cands })).ids);
      const apps = pending.filter((r) => r.status === 'app_only');
      if (apps.length) {
        const res = await api.post<{ ids: string[]; notes: string[] }>('/vocab/import-app', { items: apps.map((r) => ({ key: r.key, category: choice(r).category, vocabType: choice(r).vocabType })) });
        ids.push(...res.ids);
        notes.push(...res.notes);
        // 앱 이미지도 어드민으로(저장 순서 = 요청 순서)
        if (withImages) {
          for (let i = 0; i < apps.length; i++) {
            const keys = importableKeys(apps[i]);
            if (!keys.length || !res.ids[i]) continue;
            try {
              const r = await api.post<{ results: { outcome: string; error?: string }[] }>('/vocab/import-existing', { vocabId: res.ids[i], keys });
              r.results.filter((x) => x.outcome === 'failed').forEach((x) => notes.push(`${apps[i].text}: ${x.error}`));
            } catch (e) {
              notes.push(`${apps[i].text}: 앱 이미지를 가져오지 못했습니다(${(e as Error).message})`);
            }
          }
        }
      }
      return { ids, notes };
    },
    onSuccess: (r) => {
      if (r.notes.length) void message.warning(r.notes.join(' / '));
      onSaved(r.ids);
    },
  });
  return (
    <Modal
      open={!!purpose}
      title={purpose === 'generate' ? '이미지 생성 요청 — 먼저 관리 어휘로 저장' : '관리 어휘로 저장'}
      okText={purpose === 'generate' ? `저장하고 생성 요청으로(${rows.length})` : `저장(${pending.length})`}
      cancelText="취소"
      onCancel={onClose}
      onOk={() => save.mutate()}
      okButtonProps={{ disabled: missing.length > 0 || (purpose === 'save' && !pending.length) }}
      confirmLoading={save.isPending}
      destroyOnHidden
      width={640}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        운영 Firestore admin_vocab에 새 어휘로 저장됩니다. 앱 항목은 카드 ID를 슬러그·기존 카드로, 검색 태그를 키워드로 채우고 그 이미지들을 확정 연결로 둡니다. 핵심/주변은 저장 후 바꿀 수 없습니다.
      </Typography.Paragraph>
      {pending.length === 0 && <Alert type="info" showIcon message="선택한 어휘는 모두 관리 중입니다." />}
      {pending.map((r) => (
        <Flex key={r.key} gap={8} align="center" wrap style={{ marginBottom: 6 }} data-testid={`save-${r.key}`}>
          <Typography.Text strong style={{ minWidth: 80 }}>{r.text}</Typography.Text>
          <Tag color={STATUS_LABEL[r.status].color}>{STATUS_LABEL[r.status].label}</Tag>
          {r.status === 'app_only' && (
            <>
              <Select aria-label={`${r.text} 카테고리`} placeholder="카테고리" value={choice(r).category} status={choice(r).category ? undefined : 'error'} style={{ width: 120 }} onChange={(v) => setChoices((c) => ({ ...c, [r.key]: { ...choice(r), category: v } }))} options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
              <Segmented<'core' | 'fringe'> size="small" value={choice(r).vocabType} onChange={(v) => setChoices((c) => ({ ...c, [r.key]: { ...choice(r), vocabType: v } }))} options={[{ value: 'core', label: '핵심' }, { value: 'fringe', label: '주변' }]} />
            </>
          )}
        </Flex>
      ))}
      {pending.some((r) => r.status === 'app_only' && importableKeys(r).length) && (
        <Checkbox checked={withImages} onChange={(e) => setWithImages(e.target.checked)} style={{ marginTop: 8 }}>
          연결된 앱 이미지도 어드민으로 가져오기(검수함 '게시 완료(기존)'에서 비교, 비용 없음)
        </Checkbox>
      )}
      {missing.length > 0 && <Alert type="warning" showIcon message={`카테고리를 정해 주세요: ${missing.map((r) => r.text).join(', ')}`} />}
      {save.isError && <Alert type="error" showIcon message="저장하지 못했습니다" description={(save.error as Error).message} style={{ marginTop: 8 }} />}
    </Modal>
  );
}

/**
 * 통합 어휘 목록: 후보 CSV·관리 중 어휘·앱 기본 카드·앱 검색 이미지를 어휘 하나에 한 줄로(읽기 전용).
 * 자동 연결 규칙은 functions/src/vocab/unified.ts.
 */
export function UnifiedList({ onEdit, onGenerate, onManual, canWrite = false }: {
  onEdit?: (vocabId: string) => void;
  /** 관리 어휘 ID로 이미지 생성 요청 창 열기 */
  onGenerate?: (vocabIds: string[]) => void;
  /** asset이 있으면 그 이미지에서 다시 만들기(참고 이미지 첨부 기본) */
  onManual?: (vocabId: string, text: string, asset?: { assetId: string; previewUrl: string | null }) => void;
  canWrite?: boolean;
}) {
  const api = useApi();
  const qc = useQueryClient();
  // '새로 읽기'는 서버 캐시(5분)를 건너뛴다
  const fresh = useRef(false);
  const data = useQuery({
    queryKey: ['vocab', 'unified'],
    queryFn: () => {
      const f = fresh.current;
      fresh.current = false;
      return api.get<UnifiedData>(`/vocab/unified${f ? '?fresh=1' : ''}`);
    },
    retry: false,
  });
  const [status, setStatus] = useState<StatusFilter>('all');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [theme, setTheme] = useState<string | null>(null);
  const [showOther, setShowOther] = useState(false);
  const [confirmOnly, setConfirmOnly] = useState(false);

  const rows = useMemo(() => data.data?.rows ?? [], [data.data]);
  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: 0, managed: 0, candidate: 0, app_only: 0 };
    for (const r of rows) if (showOther || !onlyOtherPaths(r)) { c.all++; c[r.status]++; }
    return c;
  }, [rows, showOther]);
  const filtered = useMemo(
    () => rows.filter((r) =>
      (status === 'all' || r.status === status) &&
      (showOther || !onlyOtherPaths(r)) &&
      (!category || r.category === category) &&
      themeMatches(r, theme) &&
      (!confirmOnly || r.needsConfirm.cards.length + r.needsConfirm.searchImages.length > 0) &&
      matchesUnified(r, q)),
    [rows, status, showOther, category, theme, confirmOnly, q],
  );

  const refresh = () => {
    fresh.current = true;
    void qc.invalidateQueries({ queryKey: ['vocab', 'unified'] });
  };

  const [msg, holder] = message.useMessage();
  const [selected, setSelected] = useState<string[]>([]);
  const [purpose, setPurpose] = useState<'save' | 'generate' | null>(null);
  const selectedRows = rows.filter((r) => selected.includes(r.key));
  const link = useMutation({
    mutationFn: (b: { vocabId: string; key: string; action: LinkAction }) => api.post<{ changed: boolean }>('/vocab/links', b),
    onSuccess: (_r, b) => {
      msg.success(b.action === 'link' ? '연결을 확정했습니다.' : b.action === 'unlink' ? '연결을 해제했습니다.' : '자동 연결로 되돌렸습니다.');
      void qc.invalidateQueries({ queryKey: ['vocab', 'unified'] });
    },
    onError: (e) => msg.error((e as Error).message),
  });
  const importedSet = useMemo(() => new Set(data.data?.imported ?? []), [data.data]);
  const importExisting = useMutation({
    mutationFn: (b: { vocabId: string; keys: string[] }) => api.post<{ created: number; results: { outcome: string; error?: string }[] }>('/vocab/import-existing', b),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => x.outcome === 'failed');
      if (failed.length) msg.warning(`${r.created}장 가져옴, ${failed.length}장 실패: ${failed.map((f) => f.error).join(' / ')}`);
      else msg.success(`${r.created}장을 어드민으로 가져왔습니다. 검수함 '게시 완료'에서 볼 수 있습니다.`);
      void qc.invalidateQueries({ queryKey: ['vocab'] });
      void qc.invalidateQueries({ queryKey: ['review'] });
    },
    onError: (e) => msg.error((e as Error).message),
  });
  const saved = (ids: string[]) => {
    const p = purpose;
    setPurpose(null);
    setSelected([]);
    void qc.invalidateQueries({ queryKey: ['vocab'] });
    if (p === 'generate' && ids.length) onGenerate?.(ids);
    else msg.success(`관리 어휘로 저장했습니다(${ids.length}개).`);
  };
  // 모두 관리 중이면 저장할 것이 없으므로 저장 단계 없이 바로 생성 요청 창으로(#94)
  const requestGenerate = () => {
    if (selectedRows.length && selectedRows.every((r) => r.status === 'managed' && r.vocabId)) {
      const ids = selectedRows.map((r) => r.vocabId!);
      setSelected([]);
      onGenerate?.(ids);
    } else setPurpose('generate');
  };

  return (
    <Card size="small">
      {holder}
      <Flex wrap gap={8} style={{ marginBottom: 12 }} align="center">
        <Segmented<StatusFilter>
          value={status}
          onChange={setStatus}
          options={(['all', 'managed', 'candidate', 'app_only'] as const).map((s) => ({ value: s, label: `${s === 'all' ? '전체' : STATUS_LABEL[s].label} ${counts[s]}` }))}
        />
        <Input.Search allowClear placeholder="표기·슬러그·태그·카드 ID 검색" onSearch={setQ} onChange={(e) => !e.target.value && setQ('')} style={{ width: 240 }} />
        <Select allowClear placeholder="카테고리" value={category} onChange={(v) => setCategory(v ?? null)} style={{ width: 140 }} options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
        <Select allowClear placeholder="주제" aria-label="주제" value={theme} onChange={(v) => setTheme(v ?? null)} style={{ width: 200 }} options={themeOptions(rows)} />
                <Checkbox checked={confirmOnly} onChange={(e) => setConfirmOnly(e.target.checked)}>연결 확인 필요만</Checkbox>
        <Checkbox checked={showOther} onChange={(e) => setShowOther(e.target.checked)}>기타 경로(on_fct_lab·private) 포함</Checkbox>
        <Typography.Text type="secondary">{filtered.length}건</Typography.Text>
        <Button size="small" onClick={refresh} loading={data.isFetching} style={{ marginInlineStart: 'auto' }}>앱 데이터 새로 읽기</Button>
      </Flex>
      {data.data && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          앱 기본 카드: {data.data.sources.cards ? data.data.sources.releaseLabel : '읽지 않음'} · 앱 검색 이미지: {data.data.sources.searchImages ? 'generated_search_images' : '읽지 않음'}. 운영 데이터는 읽기만 합니다.
        </Typography.Paragraph>
      )}
      {canWrite && selected.length > 0 && (
        <Card size="small" style={{ marginBottom: 12, position: 'sticky', top: 0, zIndex: 2 }}>
          <Flex wrap gap={8} align="center">
            <Typography.Text strong>선택 {selected.length}개</Typography.Text>
            <Button type="primary" onClick={requestGenerate}>이미지 생성 요청</Button>
            <Button disabled={!selectedRows.some((r) => r.status !== 'managed')} onClick={() => setPurpose('save')}>관리 어휘로 저장</Button>
            <Button type="link" onClick={() => setSelected([])}>선택 해제</Button>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>후보·앱 항목은 관리 어휘로 저장한 뒤 생성합니다.</Typography.Text>
          </Flex>
        </Card>
      )}
      <SaveDialog rows={selectedRows} purpose={purpose} onClose={() => setPurpose(null)} onSaved={saved} />
      {data.data?.warnings.map((w) => <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />)}
      {data.isPending && <Skeleton active />}
      {data.isError && <Alert type="error" showIcon message="통합 목록을 불러오지 못했습니다" description={(data.error as Error).message} />}
      {data.data && (filtered.length === 0 ? <Empty description="조건에 맞는 어휘가 없습니다" /> : (
        <Table<UnifiedRow>
          size="small"
          rowKey="key"
          dataSource={filtered}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          rowSelection={canWrite ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), preserveSelectedRowKeys: true } : undefined}
          expandable={{
            expandedRowRender: (r) => (
              <Detail
                r={r}
                busy={link.isPending || importExisting.isPending}
                imported={importedSet}
                onLink={canWrite && r.vocabId ? (key, action) => link.mutate({ vocabId: r.vocabId!, key, action }) : undefined}
                onImport={canWrite && r.vocabId ? (keys) => importExisting.mutate({ vocabId: r.vocabId!, keys }) : undefined}
                onRemake={canWrite && r.vocabId && onManual ? (key, url) => {
                  const assetId = data.data?.importedAssets?.[key];
                  if (assetId) onManual(r.vocabId!, r.text, { assetId, previewUrl: url });
                } : undefined}
              />
            ),
          }}
          columns={[
            {
              title: '표기 · 슬러그',
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{r.text}{r.purpose === 'search_only' && <Tag color={PURPOSE_LABEL.search_only.color} style={{ marginInlineStart: 6 }}>{PURPOSE_LABEL.search_only.label}</Tag>}</Typography.Text>
                  <VocabHint hint={{ slug: r.slug, keywords: r.keywords }} max={3} />
                </Space>
              ),
            },
            { title: '상태', dataIndex: 'status', width: 110, render: (s: UnifiedStatus, r) => <Space size={4}><Tag color={STATUS_LABEL[s].color}>{STATUS_LABEL[s].label}</Tag>{r.vocabId && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.vocabId}</Typography.Text>}</Space> },
            { title: '카테고리', dataIndex: 'category', width: 100, render: (c: string | null) => (c ? CATEGORY_LABEL[c] ?? c : <Typography.Text type="secondary">기타</Typography.Text>) },
            { title: '앱 기본 카드', width: 90, render: (_, r) => (r.card ? <Thumb url={r.card.imageUrl} title={`${r.card.text} · ${r.card.cardId}`} /> : <Typography.Text type="secondary">-</Typography.Text>) },
            { title: '앱 검색 이미지', render: (_, r) => <SearchThumbs items={r.searchImages} /> },
            {
              title: '어드민 제작',
              render: (_, r) => (r.progress ? <Tag color={PROGRESS_LABEL[r.progress].color}>{PROGRESS_LABEL[r.progress].label}</Tag> : <Typography.Text type="secondary">-</Typography.Text>),
            },
            {
              title: '확인',
              width: 120,
              render: (_, r) => {
                const n = r.needsConfirm.cards.length + r.needsConfirm.searchImages.length;
                return (
                  <Space direction="vertical" size={0}>
                    {n > 0 && <Badge color="red" text={<Typography.Text style={{ fontSize: 12 }}>연결 확인 {n}</Typography.Text>} />}
                    {r.related.length > 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>관련 {r.related.length}</Typography.Text>}
                    {(() => {
                      const left = importableKeys(r).filter((k) => !importedSet.has(k)).length;
                      return left > 0 ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>가져올 앱 이미지 {left}</Typography.Text> : null;
                    })()}
                  </Space>
                );
              },
            },
            ...(canWrite ? [{
              title: '', key: 'act', width: 150,
              render: (_: unknown, r: UnifiedRow) => (r.vocabId ? (
                <Flex gap={4}>
                  {onEdit && <Button size="small" onClick={() => onEdit(r.vocabId!)}>수정</Button>}
                  {onManual && <Button size="small" onClick={() => onManual(r.vocabId!, r.text)}>수동 제작</Button>}
                </Flex>
              ) : null),
            }] : []),
          ]}
        />
      ))}
    </Card>
  );
}
